// Hoe een materiaal heet op het scherm.
//
// TWEE NAMEN, EEN WAARHEID. Een Materials-document draagt sinds idee #283 twee
// namen. `Title` is de naam uit de vaste keuzelijst van de meetstandaard; die
// bepaalt via materialInterventionDefaults welke maatregel erachter zit en welke
// CO2-besparing meetelt. `DisplayTitle` is de naam die de organisatie er zelf aan
// geeft ('Tochtband 9mm zwart'), omdat zeven regels die allemaal 'Tochtband
// rubber' heten in geen enkele keuzelijst uit elkaar te houden zijn.
//
// De eigen naam wint op het scherm; de standaardnaam blijft eronder staan voor de
// berekening. Daardoor kan hernoemen de CO2-koppeling niet raken -- vrij typen in
// `Title` zelf deed dat wel, en dat is precies wat inbox #119 veroorzaakte.
//
// WAAROM EEN GEDEELDE HELPER EN GEEN `DisplayTitle || Title` PER PLEK. Acht
// plekken tonen een materiaalnaam: de materialentabel, de keuzelijst bij een
// huishouden, de regel bij de bewoner, twee voorraadwaarschuwingen, de csv-export
// en de lijst 'telt niet mee'. Een van die acht overslaan levert geen fout op,
// alleen een gebruiker die op het ene scherm zijn eigen naam ziet en op het
// andere de oude.
// AI-CONTEXT: docs/pitfalls.md#dev-helper-contract-change-strands-callers
//
// GEEN TERUGVALTEKST HIERIN. Zonder enige naam komt er een lege string uit, geen
// 'Onbekend materiaal': welke tekst daar hoort verschilt per scherm, en een
// helper die dat invult neemt een beslissing die bij de afnemer thuishoort.
//
// AI-CONTEXT: See docs/patterns.md#data-display-name-beside-the-key
// AI-CONTEXT: See docs/features/materials-own-name-and-article-number.md

import { isAdvice } from './materialCatalog'

/**
 * @typedef {object} NamedMaterial
 * @property {string} [Title] - Naam uit de vaste keuzelijst van de meetstandaard
 * @property {string} [DisplayTitle] - De naam die de organisatie zelf gaf
 * @property {string} [ArticleNumber] - Het artikel- of bestelnummer van de organisatie
 * @property {string} [Kind] - 'advice' bij een coachadvies
 */

/**
 * @param {*} value
 * @returns {string} De getrimde tekst, of '' als er niets bruikbaars in stond
 */
const trimmed = (value) => (typeof value === 'string' ? value.trim() : '')

/**
 * De naam waaronder dit materiaal aan de gebruiker getoond wordt.
 *
 * @param {NamedMaterial|null|undefined} material
 * @returns {string} Lege string als er geen naam is; de afnemer kiest de terugval
 */
const materialDisplayName = (material) =>
    trimmed(material?.DisplayTitle) || trimmed(material?.Title)

/**
 * Het artikel- of bestelnummer van de organisatie, als dat ingevuld is.
 *
 * Een coachadvies koop je niet in en draagt er dus nooit een, ook niet wanneer
 * er ooit iets in het veld beland is.
 *
 * @param {NamedMaterial|null|undefined} material
 * @returns {string} Lege string als er geen nummer is
 */
const materialArticleNumber = (material) =>
    isAdvice(material) ? '' : trimmed(material?.ArticleNumber)

/**
 * De tekst waarop dit materiaal te vinden moet zijn.
 *
 * Bevat ook de standaardnaam: wie 'Tochtband rubber' intikt hoort zijn eigen
 * 'Tochtband 9mm zwart' nog te vinden -- het is hetzelfde product, alleen anders
 * genoemd. En het productnummer, want dat is vaak het enige waarmee twee dozen
 * van hetzelfde product uit elkaar te houden zijn.
 *
 * @param {NamedMaterial|null|undefined} material
 * @returns {string}
 */
const materialSearchText = (material) => {
    const shown = materialDisplayName(material)
    const standard = trimmed(material?.Title)
    const article = materialArticleNumber(material)

    const delen = [shown]
    if (standard && standard !== shown) delen.push(standard)
    if (article) delen.push(article)

    return delen.filter(Boolean).join(' ')
}

export { materialSearchText, materialArticleNumber }
export default materialDisplayName
